import { ApiInstance, RetrySettings } from '@src/services/api-instance';
import { getAuthToken } from '@src/services/auth-service';
import { BrainResourceResponseDTO } from './dto/brain-resources.get.response.dto';
import {
  BrainResourceGetDefinitionsByResourceIdResponseDTO,
  ResourceDefinitionDTO,
} from './dto/brain-resources.getDefinitionsByResourceId.response.dto';
import { BrainResourcePostDefinitionsByResourceIdBodyDTO } from './dto/brain-resources.postDefinitionsByResourceId.body.dto';

export class BrainResourcesService {
  private readonly client: ApiInstance;

  constructor(client: ApiInstance) {
    this.client = new ApiInstance(`${client.getBaseUrl()}/resources`, client.getTimeout());
  }

  async getById(request: {
    id: string;
    params?: {
      includeCurrentDefinitions?: boolean;
      onlyPublished?: boolean;
    };
    settings?: RetrySettings;
  }): Promise<BrainResourceResponseDTO> {
    return (
      await this.client.get<BrainResourceResponseDTO>(`${request.id}`, request.params, {
        bearerToken: await getAuthToken(),
        settings: request.settings,
      })
    ).data;
  }

  async getDefinitionsByResourceId(request: {
    resourceId: string;
    params: {
      organizationId: string;
      startDate?: string;
      endDate?: string;
      sortOrder?: 'asc' | 'desc';
      sortBy?: 'createdAt' | 'version';
      limit?: number;
      currentPage?: number;
    };
    settings?: RetrySettings;
  }): Promise<BrainResourceGetDefinitionsByResourceIdResponseDTO> {
    return (
      await this.client.get<BrainResourceGetDefinitionsByResourceIdResponseDTO>(
        `${request.resourceId}/definitions`,
        request.params,
        {
          bearerToken: await getAuthToken(),
          settings: request.settings,
        },
      )
    ).data;
  }

  async postPublishDefinitionById(request: {
    resourceId: string;
    resourceDefinitionId: string;
    settings?: RetrySettings;
  }): Promise<ResourceDefinitionDTO> {
    return (
      await this.client.post<undefined, ResourceDefinitionDTO>(
        `${request.resourceId}/definitions/${request.resourceDefinitionId}/publish`,
        undefined,
        {
          bearerToken: await getAuthToken(),
          settings: request.settings,
        },
      )
    ).data;
  }

  async postDefinitionByResourceId(request: {
    resourceId: string;
    body: BrainResourcePostDefinitionsByResourceIdBodyDTO;
    settings?: RetrySettings;
  }): Promise<ResourceDefinitionDTO> {
    return (
      await this.client.post<BrainResourcePostDefinitionsByResourceIdBodyDTO, ResourceDefinitionDTO>(
        `${request.resourceId}/definitions`,
        request.body,
        {
          bearerToken: await getAuthToken(),
          settings: request.settings,
        },
      )
    ).data;
  }
}
